import { endOfMonth, format, parse, startOfMonth } from 'date-fns';
import { ApolloProvider, useQuery } from '@apollo/client';
import React, { useEffect, useMemo } from 'react';
import { WrappedSpinner } from 'spinner';

import { useClient } from '../bk-web-app/ClientContext';
import { newCompileQueryData } from './compileQueryData';
import { DateRange, GeneralLedgerFilters, SidebarDescriptor } from './types';
import {
  apolloV2Client,
  GET_ALL_GL_DATA,
  GET_ALL_JOURNAL_ENTRIES,
  GET_CONFIRM_CATEGORY_TASKS,
  GET_LEDGER_ACCOUNTS,
  GET_LEDGER_ACCOUNTS_BY_CLIENT,
} from './queries';
import SelectDateRangeNullState from './SelectDateRangeNullState';
import { ErrorMessage } from '../client-web-app/components';
import { GET_CONTRACTOR_LIST } from './components/report-1099-form/graphql';
import { useV2Query } from 'utils';
import GeneralLedgerWithContext from './GeneralLedgerWithContext';
import { useGeneralLedgerPerformanceTracking } from './utils/performanceTracking';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
import { SplitFeatureFlags } from 'bk-web-app/features';

const dateFormat = 'YYYY-MM-DD';

const expandDateRangeToSpanWholeMonths = (dateRange: DateRange): DateRange => {
  const endDateAtEndOfMonth =
    dateRange && dateRange.endDate && format(endOfMonth(parse(dateRange.endDate)), dateFormat);
  const startDateAtStartOfMonth =
    dateRange &&
    dateRange.startDate &&
    format(startOfMonth(parse(dateRange.startDate)), dateFormat);
  return {
    endDate: endDateAtEndOfMonth,
    startDate: startDateAtStartOfMonth,
  };
};

type GeneralLedgerContainerProps = {
  clientId: string;
  dateRange?: DateRange;
  filters?: GeneralLedgerFilters;
  updateQueryParams: (obj: Record<string, unknown>) => void;
  updateSidebarParams: (obj?: Record<string, unknown>) => void;
  sidebarDescriptor?: SidebarDescriptor;
};

const GeneralLedgerContainer = ({
  clientId,
  dateRange,
  filters,
  updateQueryParams,
  updateSidebarParams,
  sidebarDescriptor,
}: GeneralLedgerContainerProps) => {
  const client = useClient();
  const performanceTracker = useGeneralLedgerPerformanceTracking();

  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;

  const isConfirmCategorizationTasksEnabled =
    features[SplitFeatureFlags.CONFIRM_CATEGORIZATION_TASKS]?.value === FeatureValue.FEATURE_ON;

  const isAssignCategoryImprovementEnabled =
    features[SplitFeatureFlags.ASSIGN_CATEGORY_IMPROVEMENT]?.value === FeatureValue.FEATURE_ON;

  const isLightningGLEnabled = client.featureFlags.lightningGL;

  const fullDateRange = dateRange && expandDateRangeToSpanWholeMonths(dateRange);
  const variables = {
    clientId,
    fromDate: fullDateRange?.startDate,
    toDate: fullDateRange?.endDate,
    filterZeroEntryLedgers: true,
  };

  // "skip: !clientId" has been added below top enforce that the query is called only if clientId exist
  // Currently, it is not making sure that clientId is always available. Hence, we have to add this for now.
  // Feel Free to remove this, in case you made sure that clientId will always be available here
  const {
    loading: contractorsLoading,
    error: contractorsError,
    data: contractorData,
  } = useV2Query(GET_CONTRACTOR_LIST, {
    variables: { clientId },
    skip: !clientId,
    onCompleted: data => {
      if (fullDateRange && clientId) {
        performanceTracker.dataFetchEnd(
          'contractors',
          fullDateRange,
          data?.contractors?.length,
          clientId,
        );
      }
    },
  });
  const {
    loading,
    error,
    data: glData,
  } = useQuery(GET_ALL_GL_DATA, {
    variables,
    // @ts-expect-error - this works but it expect a different object.
    client: apolloV2Client,
    onCompleted: data => {
      if (fullDateRange && clientId) {
        const dataSize = data?.aggregateBalanceSummaries?.length || 0;
        performanceTracker.dataFetchEnd('glData', fullDateRange, dataSize, clientId);
      }
    },
  });

  const {
    loading: entriesLoading,
    error: entriesError,
    data: entryData,
  } = useQuery(GET_ALL_JOURNAL_ENTRIES, {
    variables,
    // @ts-expect-error - this works but it expect a different object.
    client: apolloV2Client,
    onCompleted: data => {
      if (fullDateRange && clientId) {
        const dataSize = data?.tempAllJournalEntries?.journalEntries?.length || 0;
        performanceTracker.dataFetchEnd('journalEntries', fullDateRange, dataSize, clientId);
      }
    },
  });

  // Use v2 query if feature flag is enabled, otherwise use v1
  const ledgerAccountsQuery = isAssignCategoryImprovementEnabled
    ? GET_LEDGER_ACCOUNTS_BY_CLIENT
    : GET_LEDGER_ACCOUNTS;

  const {
    loading: ledgerAccountsLoading,
    error: ledgerAccountsError,
    data: ledgerAccountsData,
  } = useQuery(ledgerAccountsQuery, {
    variables: { clientId: variables.clientId },
    // @ts-expect-error - this works but it expect a different object.
    client: apolloV2Client,
    context: isAssignCategoryImprovementEnabled ? { useV2: true } : undefined,
    onCompleted: data => {
      if (fullDateRange && clientId) {
        const accounts = isAssignCategoryImprovementEnabled
          ? data?.ledgerAccountsByClient
          : data?.ledgerAccounts;
        performanceTracker.dataFetchEnd(
          'ledgerAccounts',
          fullDateRange,
          accounts?.length || 0,
          clientId,
        );
      }
    },
  });

  // Extract ledger accounts from response based on which query was used
  const ledgerAccounts = isAssignCategoryImprovementEnabled
    ? ledgerAccountsData?.ledgerAccountsByClient
    : ledgerAccountsData?.ledgerAccounts;

  // Fetch confirm category tasks
  const {
    loading: tasksLoading,
    error: tasksError,
    data: tasksData,
  } = useV2Query(GET_CONFIRM_CATEGORY_TASKS, {
    variables: { clientId: variables.clientId },
    skip: !clientId || !isConfirmCategorizationTasksEnabled,
    onCompleted: data => {
      if (fullDateRange && clientId) {
        const dataSize = data?.tasks?.tasks?.length || 0;
        performanceTracker.dataFetchEnd('confirmCategoryTasks', fullDateRange, dataSize, clientId);
      }
    },
  });

  // Start performance tracking on mount
  useEffect(() => {
    if (fullDateRange && clientId) {
      performanceTracker.pageLoadStart(clientId);
      performanceTracker.dataFetchStart('contractors', fullDateRange, clientId);
      performanceTracker.dataFetchStart('glData', fullDateRange, clientId);
      performanceTracker.dataFetchStart('journalEntries', fullDateRange, clientId);
      performanceTracker.dataFetchStart('ledgerAccounts', fullDateRange, clientId);
      if (isConfirmCategorizationTasksEnabled) {
        performanceTracker.dataFetchStart('confirmCategoryTasks', fullDateRange, clientId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount

  const journalEntries = entryData?.tempAllJournalEntries?.journalEntries;

  const compiledData = useMemo(() => {
    if (!glData || !entryData || !contractorData || !ledgerAccounts) return null;

    const startTime = performance.now();

    // Filter tasks based on taskStatusFilter
    const allTasks = tasksData?.tasks?.tasks || [];
    let filteredTasks = allTasks;

    if (filters?.taskStatusFilter === 'pending') {
      // Only include PENDING_WORK tasks
      filteredTasks = allTasks.filter(task => task.status === 'PENDING_WORK');
    } else if (filters?.taskStatusFilter === 'confirmed') {
      // Only include RESOLVED with CATEGORY_CONFIRMED resolution
      filteredTasks = allTasks.filter(
        task => task.status === 'RESOLVED' && task.resolution === 'CATEGORY_CONFIRMED',
      );
    } else if (filters?.taskStatusFilter === 'all') {
      // Include both PENDING_WORK and RESOLVED with CATEGORY_CONFIRMED
      filteredTasks = allTasks.filter(
        task =>
          task.status === 'PENDING_WORK' ||
          (task.status === 'RESOLVED' && task.resolution === 'CATEGORY_CONFIRMED'),
      );
    }
    // If taskStatusFilter is 'none' or undefined, pass all tasks (they'll be filtered out in compileQueryData)

    const result = newCompileQueryData(
      glData.addOns,
      glData.aggregateBalanceSummaries,
      journalEntries,
      glData.categorizationInconsistencies,
      glData.ledgerAccountCategoryOrders,
      contractorData.contractors,
      ledgerAccounts,
      filteredTasks,
    );

    const endTime = performance.now();

    if (fullDateRange && clientId) {
      const totalDataSize =
        (journalEntries?.length || 0) + (glData.aggregateBalanceSummaries?.length || 0);
      performanceTracker.trackDataProcessingDuration(
        'queryDataCompilation',
        startTime,
        endTime,
        fullDateRange,
        totalDataSize,
        clientId,
      );
    }

    return result;
  }, [
    glData,
    entryData,
    contractorData,
    ledgerAccounts,
    tasksData,
    journalEntries,
    fullDateRange,
    clientId,
    filters?.taskStatusFilter,
    performanceTracker,
  ]);

  const dataEntries = compiledData?.dataEntries;
  const ledgerAccountCategories = compiledData?.ledgerAccountCategories;
  const dataEntriesMap = compiledData?.maps?.dataEntriesMap;
  const moneyInTransitLedgerId = compiledData?.moneyInTransitLedgerId;
  const potentialMatches = compiledData?.potentialMatches;
  const hasQualityIssues = glData?.categorizationInconsistencies?.length > 0;

  useEffect(() => {
    if (
      !loading &&
      !entriesLoading &&
      !contractorsLoading &&
      !ledgerAccountsLoading &&
      !tasksLoading &&
      fullDateRange &&
      clientId &&
      dataEntries
    ) {
      const totalDataSize = dataEntries.length + (journalEntries?.length || 0);
      performanceTracker.pageLoadEnd(fullDateRange, totalDataSize, clientId);
    }
  }, [
    loading,
    entriesLoading,
    contractorsLoading,
    ledgerAccountsLoading,
    tasksLoading,
    fullDateRange,
    clientId,
    dataEntries,
    journalEntries,
    performanceTracker,
  ]);

  if (!fullDateRange || !fullDateRange.startDate || !fullDateRange.endDate) {
    return <SelectDateRangeNullState updateDateRange={updateQueryParams} />;
  }

  if (loading || entriesLoading || contractorsLoading || ledgerAccountsLoading || tasksLoading) {
    return <WrappedSpinner />;
  }

  if (error || entriesError || contractorsError || ledgerAccountsError || tasksError) {
    let errorMessage;
    if (error?.networkError && 'result' in error.networkError) {
      errorMessage = `${error.networkError.result?.errors[0]?.message}`;
    } else {
      errorMessage = 'unknown error';
    }
    return (
      <ErrorMessage
        minHeight={60}
        message={`There was an error fetching data for the general ledger: ${errorMessage}`}
      />
    );
  }

  if (!compiledData || !dataEntries || !ledgerAccountCategories || !dataEntriesMap) {
    return <WrappedSpinner />;
  }

  return (
    <>
      {/* @ts-expect-error - the client works here, but requires a type.*/}
      <ApolloProvider client={apolloV2Client}>
        <GeneralLedgerWithContext
          dataEntries={dataEntries}
          dateRange={fullDateRange}
          defaultFilters={filters}
          defaultSidebarDescriptor={sidebarDescriptor}
          hasQualityIssues={hasQualityIssues}
          ledgerAccountCategories={ledgerAccountCategories}
          onFiltersChange={updateQueryParams}
          updateSidebarParams={updateSidebarParams}
          updateDateRange={updateQueryParams}
          dataEntriesMap={dataEntriesMap}
          isLightningGL={isLightningGLEnabled}
          client={client}
          moneyInTransitLedgerId={moneyInTransitLedgerId}
          potentialMatches={potentialMatches}
          confirmCategoryTasks={compiledData.confirmCategoryTasks}
        />
      </ApolloProvider>
    </>
  );
};

GeneralLedgerContainer.defaultProps = {
  dateRange: undefined,
  filters: undefined,
  sidebarDescriptor: undefined,
};

export default GeneralLedgerContainer;
